import CheckCircle from "@assets/images/check-circle.svg";
import InfoIcon from "@assets/images/info.svg";
import RefreshIcon from "@assets/images/refresh-primary.svg";
import ErrorIcon from "@assets/images/x-circle.svg";
import { Box, Button, Divider, Grid } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import InputSuggestion from "@saleor/alternatedMaterial/components/InputSuggestionForPrioridy";
import CollapseFilter from "@saleor/components/CollapseFilter";
import FormSpacer from "@saleor/components/FormSpacer";
import MyTypography from "@saleor/components/MyTypography";
import { SingleSelectField } from "@saleor/components/SingleSelectField";
import {
  GetStockOnHandReportQueryResult,
  useFilterRequireAttentionMaterialLazyQuery
} from "@saleor/graphql";
import { StatusFilter } from "@saleor/requiredAttention/types";
import { filterStockOnHand } from "@saleor/stockOnHand/views/ReportStockOnHandPage";
import { mapEdgesToItems } from "@saleor/utils/maps";
import classNames from "classnames";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import SVG from "react-inlinesvg";
import { FormattedMessage, useIntl } from "react-intl";

import useStyles from "./styles";

interface StockOnHandFilterProps {
  statusFilter: StatusFilter;
  setFilter: (value: filterStockOnHand) => void;
  filterArr: any[];
  loading: boolean;
  dataStockOnHand: GetStockOnHandReportQueryResult["data"]["getStockOnHandReport"];
  setFilterPlant: (string) => void;
}

const StockOnHandFilter: React.FC<StockOnHandFilterProps> = props => {
  const {
    statusFilter,
    filterArr,
    setFilter,
    loading,
    dataStockOnHand,
    setFilterPlant
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isClean, setIsClean] = useState<boolean>(false);

  const intl = useIntl();
  const classes = useStyles();

  useEffect(() => {
    if (!loading) {
      if (filterArr.filter(item => item).length > 1 && dataStockOnHand) {
        setIsOpen(false);
      }
    }
  }, [loading]);

  const formik = useFormik({
    initialValues: {
      materialGradeGram: "",
      unit: "ROL",
      type: "Customer"
    },
    onSubmit: value => {
      const messageError = intl.formatMessage({
        defaultMessage: "โปรดระบุข้อมูล"
      });
      let flagError = false;
      if (!value.materialGradeGram) {
        formik.setFieldError("materialGradeGram", messageError);
        flagError = true;
      }
      if (!value.unit) {
        formik.setFieldError("unit", messageError);
        flagError = true;
      }
      if (!flagError) {
        setFilter(value);
      }
    }
  });

  const [
    getMaterialGradeGramDataSuggestions,
    {
      data: fetchMaterialGradeGramDataSuggestions,
      loading: loadingMaterialGradeGramDataSuggestion
    }
  ] = useFilterRequireAttentionMaterialLazyQuery();

  const dataSearchSuggestion =
    fetchMaterialGradeGramDataSuggestions?.filterRequireAttentionMaterial;

  const unitChoices = [
    { label: "ROL", value: "ROL" },
    { label: "TON", value: "TON" }
  ];

  const handleChangeMaterial = ({ value }) => {
    formik.handleChange({
      target: {
        value: value ?? "",
        name: "materialGradeGram"
      }
    });
  };

  const renderMessase = () => {
    let srcImg = null;
    let message;
    let classNameMessage = "";
    const defaultMessase = (
      <div>
        <span>โปรดกรอกข้อมูลที่จำเป็น (</span>
        <span className={classes.requiredMark}>*</span>
        <span>) และกดค้นหา</span>
      </div>
    );
    switch (statusFilter) {
      case StatusFilter.INFO:
        srcImg = InfoIcon;
        message = defaultMessase;
        classNameMessage = classes.colorInfo;
        break;
      case StatusFilter.SUCCESS:
        srcImg = CheckCircle;
        message = intl.formatMessage({
          defaultMessage: "พบรายการที่เกี่ยวข้อง"
        });
        classNameMessage = classes.colorSuccess;
        break;
      case StatusFilter.ERROR:
        srcImg = ErrorIcon;
        message = intl.formatMessage({
          defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
        });
        classNameMessage = classes.colorError;
        break;
      default:
        srcImg = InfoIcon;
        message = defaultMessase;
        break;
    }
    return (
      <Box className={classes.info}>
        <img width={20} src={srcImg} alt="info" className={classes.icon} />
        <MyTypography
          variant="body3"
          align={"center"}
          className={classNameMessage}
        >
          {message}
        </MyTypography>
      </Box>
    );
  };

  const isDisable = useMemo(() => !formik.dirty || loading, [
    formik.dirty,
    loading
  ]);

  const handleResetForm = () => {
    formik.resetForm();
    if (setFilter) {
      setFilter({ materialGradeGram: "", unit: "", type: "Customer" });
    }
    setIsClean(true);
    setFilterPlant("All");
    setTimeout(() => setIsClean(false));
  };

  const handleSubmitForm = () => {
    setFilterPlant("All");
    formik.handleSubmit();
  };

  return (
    <>
      <CollapseFilter
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        label={intl.formatMessage({
          defaultMessage: "ค้นหา Stock on-hand"
        })}
      >
        <Grid className={classes.searchBox} container spacing={5}>
          <Grid item xs={6} md={8}>
            <MyTypography variant={"subtitle3"}>
              {intl.formatMessage({
                defaultMessage: "Material No. / Grade-Gram"
              })}
              <span className={classes.requiredMark}>*</span>
            </MyTypography>
            <InputSuggestion
              id="materialGradeGram"
              pageInfo={dataSearchSuggestion?.pageInfo}
              showLoadMore={true}
              fetchDataSuggestions={fetchMaterialGradeGramDataSuggestions}
              loadingDataSuggestion={loadingMaterialGradeGramDataSuggestion}
              getDataSuggestions={getMaterialGradeGramDataSuggestions}
              searchField={"material"}
              isCheckingRole
              dataOptions={
                mapEdgesToItems(dataSearchSuggestion)?.map(data => ({
                  title: `${data.code} - ${data.descriptionEn}`,
                  value: data.code
                })) || []
              }
              contained={false}
              name={"materialGradeGram"}
              withNonSelect
              objectSuggestion
              placeholder={"ค้นหา Material No. / Grade-Gram"}
              error={!!formik.errors.materialGradeGram}
              isClean={isClean}
              handleSelectedSearch={handleChangeMaterial}
            />
            {!!formik.errors.materialGradeGram ? (
              <MyTypography variant={"caption"} className={classes.textError}>
                {formik.errors.materialGradeGram}
              </MyTypography>
            ) : null}
          </Grid>
          <Grid item xs={6} md={4}>
            <MyTypography variant={"subtitle3"}>
              {intl.formatMessage({
                defaultMessage: "Unit"
              })}
              <span className={classes.requiredMark}>*</span>
            </MyTypography>
            <SingleSelectField
              id="unit"
              placeholder={"เลือก Unit"}
              name={"unit"}
              choices={unitChoices}
              onChange={formik.handleChange}
              value={formik.values.unit}
              error={!!formik.errors.unit}
            />
            {!!formik.errors.unit ? (
              <MyTypography variant={"caption"} className={classes.textError}>
                {formik.errors.unit}
              </MyTypography>
            ) : null}
          </Grid>
        </Grid>

        <Box className={classes.divider}>
          <FormSpacer />
          <Divider />
        </Box>

        <Box className={classes.bottomBar} ml={3}>
          {renderMessase()}
          <Box className={classes.flex}>
            <Box mr={2}>
              <Button
                id="resetButton"
                data-testid="resetButton"
                disabled={isDisable}
                className={classNames(
                  classes.actionButton,
                  isDisable && classes.disableBtn
                )}
                variant={"outlined"}
                color={"secondary"}
                onClick={handleResetForm}
              >
                <SVG src={RefreshIcon} width={24} height={24} />
                <FormattedMessage defaultMessage="ล้างข้อมูล" />
              </Button>
            </Box>
            <Box>
              <Button
                id="submitButton"
                data-testid="submitButton"
                className={classes.actionButton}
                variant={"contained"}
                color={"secondary"}
                onClick={handleSubmitForm}
                disabled={isDisable}
              >
                <SearchIcon style={{ fontSize: "2.4rem" }} />
                <FormattedMessage defaultMessage="ค้นหา" />
              </Button>
            </Box>
          </Box>
        </Box>
      </CollapseFilter>
    </>
  );
};

export default StockOnHandFilter;
